<template>
  <div
    class="v2-wishlist-products-page"
    :class="{
      'v2-wishlist-products-page--public': publicToken,
      'v2-wishlist-products-page--kp3462': isKp3462Layout,
    }"
  >
    <!-- Loading -->
    <div
      v-if="!isLoaded"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading products..."
      />
    </div>

    <!-- Error -->
    <div
      v-else-if="error"
      class="v2-flex v2-justify-center v2-items-center v2-p-12"
    >
      <v2-card class="v2-text-center v2-p-8">
        <h3 class="v2-text-destructive">
          {{ error }}
        </h3>
      </v2-card>
    </div>

    <!-- Content -->
    <div
      v-else
      class="v2-wishlist-products-page__loaded"
      :class="{ 'v2-wishlist-products-page--disabled': isPerformingAction }"
    >
      <!-- KP-3462 ON: full-width toolbar above the filter + grid (title + all
           controls). Title sits at its left (brand/retailer); public keeps its
           own title row. -->
      <div
        v-if="isKp3462Layout"
        class="v2-wishlist-products-page__controls"
        :class="{ 'v2-wishlist-products-page__controls--collapsed': isHeaderCollapsed }"
      >
        <div class="v2-wishlist-products-page__left-controls">
          <!-- Title moved onto the toolbar row (brand/retailer view). -->
          <div
            v-if="!publicToken"
            class="v2-wishlist-products-page__header-title"
          >
            <button
              class="v2-wishlist-products-page__back-btn"
              @click="goBack"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line
                  x1="19"
                  y1="12"
                  x2="5"
                  y2="12"
                />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>
            <h1
              class="v2-heading-sm v2-m-0 v2-wishlist-products-page__title-text"
              :title="wishlist.name || getWishlistTitle()"
            >
              {{ wishlist.name || getWishlistTitle() }}
            </h1>
          </div>

          <!-- KP-3289: public header shows the wishlist name. -->
          <template v-if="publicToken">
            <div class="v2-wishlist-products-page__titlerow">
              <span class="v2-wishlist-products-page__brand-title">
                {{ wishlist.name || getWishlistTitle() }}
              </span>
              <span class="v2-wishlist-products-page__count d-inline-block d-sm-none">
                {{ totalProducts }} {{ totalProducts === 1 ? 'style' : 'styles' }}
              </span>
            </div>
            <span class="v2-wishlist-products-page__brand-divider" />
          </template>

          <div class="v2-wishlist-products-page__search">
            <svg
              class="v2-wishlist-products-page__search-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="11"
                cy="11"
                r="8"
              />
              <line
                x1="21"
                y1="21"
                x2="16.65"
                y2="16.65"
              />
            </svg>
            <v2-input
              :value="filters.search"
              :placeholder="searchBarPlaceholderText"
              @input="filters.search = $event; searchProducts()"
            />
          </div>

          <!-- Group Products by — shown on the public wishlist and for retailers. -->
          <div
            v-if="publicToken || !isBrand"
            class="v2-flex v2-items-center v2-gap-2 v2-wishlist-products-page__group-by"
          >
            <span class="v2-text-sm v2-text-muted v2-wishlist-products-page__group-by-label d-none d-lg-block">Group Products by:</span>
            <v2-dropdown
              :options="groupOptions"
              :value="groupByDisplayValue"
              placeholder="Group Products by"
              @select="selectedGroup = $event"
            />
          </div>

          <!-- Select All -->
          <v2-button
            v-if="!publicToken && products.length > 0"
            variant="outline"
            @click="onToggleSelectAll"
          >
            <svg
              v-if="!isSelectedAllOnScreen"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {{ isSelectedAllOnScreen ? 'Deselect all' : 'Select all' }}
          </v2-button>

          <!-- Share Wishlist -->
          <v2-button
            v-if="isBrand && !isReadOnly"
            variant="primary"
            @click="onClickShareWishlist"
          >
            Share Wishlist
          </v2-button>

          <!-- KP-3861: what a recipient sees — which prices, and which products
               the email preview shows, in what order. Brand-only: these decide
               what the OTHER side of a share is shown. -->
          <v2-button
            v-if="isBrand && !isReadOnly"
            variant="outline"
            @click="showPreviewSettings = true"
          >
            Sharing &amp; email preview
          </v2-button>

          <!-- Currency dropdown -->
          <div
            v-if="isNotPublicTokenWithRetailer && wishlist.availableCurrencies && wishlist.availableCurrencies.length"
            class="v2-wishlist-products-page__currency"
          >
            <currency-dropdown
              :base-currency="wishlistBaseCurrency"
              :selected-currency="filters.currency"
              :currencies="wishlist.availableCurrencies || []"
              @on-selected-currency="filterByCurrency"
            />
          </div>

          <!-- KP-3360: Filter By Delivery Date (desktop; moves into the filter
               sheet on mobile/tablet via the #before-filters slot). -->
          <div
            v-if="filterOptions.availabilityDates && filterOptions.availabilityDates.length"
            class="v2-wishlist-products-page__dd-desktop"
            :class="{ 'v2-wishlist-products-page__dd-desktop--mobile': !hasFilterOptions }"
          >
            <v2-filter-by-delivery-date
              :availability-dates="filterOptions.availabilityDates"
              :value="selectedDeliveryDateIndexes"
              @apply="applyDeliveryDateFilter"
            />
          </div>
        </div>

        <div class="v2-flex v2-items-center v2-gap-2 v2-wishlist-products-page__right-controls">
          <!-- Filter trigger — only shown below the desktop breakpoint. -->
          <button
            v-if="hasFilterOptions"
            type="button"
            class="v2-wishlist-products-page__filter-toggle d-inline-flex d-lg-none"
            @click="openSideBar"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line
                x1="4"
                y1="6"
                x2="20"
                y2="6"
              />
              <line
                x1="7"
                y1="12"
                x2="17"
                y2="12"
              />
              <line
                x1="10"
                y1="18"
                x2="14"
                y2="18"
              />
            </svg>
            <span class="v2-wishlist-products-page__filter-label">Filter</span>
            <span
              v-if="activeFilterCount"
              class="v2-wishlist-products-page__filter-badge"
            >{{ activeFilterCount }}</span>
          </button>

          <!-- View Toggle - Grid Icon -->
          <button
            class="v2-wishlist-products-page__view-btn d-none d-sm-flex"
            :class="{ 'v2-wishlist-products-page__view-btn--active': itemViewClass === PRODUCTS_VIEW.GRID_VIEW }"
            @click="onGridViewClick"
          >
            <large-grid-view-icon />
          </button>

          <!-- View Toggle - Compact Icon -->
          <button
            class="v2-wishlist-products-page__view-btn d-none d-sm-flex"
            :class="{ 'v2-wishlist-products-page__view-btn--active': itemViewClass === PRODUCTS_VIEW.COMPACT_GRID_VIEW }"
            @click="onCompactViewClick"
          >
            <compact-grid-view-icon />
          </button>

          <!-- Sort -->
          <v2-dropdown
            :options="visibleSortByOptions"
            :value="selectedSortBy"
            label-key="text"
            value-key="value"
            align="right"
            @select="onSortChange"
          />
        </div>
      </div>

      <!-- KP-3462 OFF: original header (Back + Title). -->
      <div
        v-else-if="!publicToken"
        class="v2-wishlist-products-page__header"
      >
        <button
          class="v2-wishlist-products-page__back-btn"
          @click="goBack"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <line
              x1="19"
              y1="12"
              x2="5"
              y2="12"
            />
            <polyline points="12 19 5 12 12 5" />
          </svg>
        </button>
        <h1 class="v2-heading-sm v2-m-0">
          {{ wishlist.name || getWishlistTitle() }}
        </h1>
      </div>

      <div class="v2-flex v2-flex-row v2-wishlist-products-page__main">
        <!-- Sidebar filter -->
        <template v-if="hasFilterOptions || publicToken">
          <!-- Mobile/tablet: dim scrim behind the filter sheet. Rendered on all
               sizes but only visible below the desktop breakpoint (CSS). -->
          <div
            v-if="hasFilterOptions"
            class="v2-wishlist-products-page__scrim"
            :class="{ 'v2-wishlist-products-page__scrim--show': mqShallShowLeftSidebar }"
            @click="closeSideBar"
          />

          <!-- Filter sidebar skeleton (desktop only). On the public wishlist the
               facets are derived from a slower follow-up product fetch
               (setupPublicFilterOptions), so the filter column otherwise pops in
               late and shifts the grid. `filterSetup` flips true up-front as a
               re-entry guard, so it can't gate this; `hasFilterOptions` only
               becomes true once filterOptions is actually populated. So: show
               the shimmer while setup is in flight but the facets aren't ready,
               then swap to the real <shop-filter>. -->
          <div
            v-if="filterSetup && !hasFilterOptions"
            class="v2-wishlist-products-page__filter-skeleton"
            aria-hidden="true"
          >
            <div
              v-for="block in 4"
              :key="`sk-block-${block}`"
              class="v2-wishlist-products-page__sk-block"
            >
              <div class="v2-wishlist-products-page__sk-title" />
              <div
                v-for="line in 4"
                :key="`sk-line-${block}-${line}`"
                class="v2-wishlist-products-page__sk-line"
              />
            </div>
            <div class="v2-wishlist-products-page__sk-slider" />
          </div>

          <div
            v-if="hasFilterOptions"
            class="v2-wishlist-products-page__sidebar"
            :class="{ 'v2-wishlist-products-page__sidebar--open': mqShallShowLeftSidebar }"
          >
            <!-- Bottom-sheet grip (mobile/tablet affordance; hidden on desktop). -->
            <span
              class="v2-wishlist-products-page__sheet-grip"
              @click="closeSideBar"
            />

            <shop-filter
              v-if="hasFilterOptions"
              :filters="filters"
              :total-products="totalProducts"
              :filter-options="filterOptions"
              :whole-sale-enable="isShowWsp && wspAllowedForViewer"
              :mq-shall-show-left-sidebar.sync="mqShallShowLeftSidebar"
              @update-filters="fetchShopProducts"
              @close-sidebar="closeSideBar"
            >
              <!-- KP-3360: on mobile/tablet the delivery-date filter lives
                   inside the filter sheet (above the filter sections); on
                   desktop it stays in the header controls. The wrapper is
                   display:none on desktop so the desktop left sidebar is
                   unchanged. -->
              <template #before-filters>
                <!-- d-lg-none: on desktop the delivery-date filter lives only in
                     the top toolbar (__dd-desktop); this in-panel copy is for the
                     mobile/tablet filter sheet. The global Bootstrap utility wins
                     even though the scoped rule can't reliably reach slotted
                     content rendered inside ShopFilter. -->
                <div
                  v-if="filterOptions.availabilityDates && filterOptions.availabilityDates.length"
                  class="v2-wishlist-products-page__sheet-dd d-lg-none"
                >
                  <span class="v2-wishlist-products-page__sheet-dd-label">Delivery Date</span>
                  <v2-filter-by-delivery-date
                    :availability-dates="filterOptions.availabilityDates"
                    :value="selectedDeliveryDateIndexes"
                    @apply="applyDeliveryDateFilter"
                  />
                </div>
              </template>
            </shop-filter>

            <!-- Sticky apply footer (mobile/tablet only). Filters apply live, so
                 this just dismisses the sheet back to the results. -->
            <div class="v2-wishlist-products-page__sheet-foot">
              <button
                type="button"
                class="v2-wishlist-products-page__sheet-apply"
                @click="closeSideBar"
              >
                Show {{ totalProducts }} {{ totalProducts === 1 ? 'result' : 'results' }}
              </button>
            </div>
          </div>

          <!-- Main content -->
          <div class="v2-wishlist-products-page__content">
            <!-- Controls bar. On mobile it collapses (name line slides away) on
                 scroll-down (--collapsed) and expands on scroll-up.
                 KP-3462 OFF only — when ON these controls live in the
                 full-width toolbar at the top of the page. -->
            <div
              v-if="!isKp3462Layout"
              class="v2-wishlist-products-page__controls"
              :class="{ 'v2-wishlist-products-page__controls--collapsed': isHeaderCollapsed }"
            >
              <div class="v2-wishlist-products-page__left-controls">
                <!-- KP-3289: public header shows the wishlist name. -->
                <template v-if="publicToken">
                  <!-- Option A "Refined Glass" title row: name + style count.
                       Collapses on scroll (mobile). The wrapper is
                       display:contents on desktop so the name still flows inline
                       there, and the count is desktop-hidden. -->
                  <div class="v2-wishlist-products-page__titlerow">
                    <span class="v2-wishlist-products-page__brand-title">
                      {{ wishlist.name || getWishlistTitle() }}
                    </span>
                    <span class="v2-wishlist-products-page__count d-inline-block d-sm-none">
                      {{ totalProducts }} {{ totalProducts === 1 ? 'style' : 'styles' }}
                    </span>
                  </div>
                  <span class="v2-wishlist-products-page__brand-divider" />
                </template>

                <div class="v2-wishlist-products-page__search">
                  <svg
                    class="v2-wishlist-products-page__search-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <circle
                      cx="11"
                      cy="11"
                      r="8"
                    />
                    <line
                      x1="21"
                      y1="21"
                      x2="16.65"
                      y2="16.65"
                    />
                  </svg>
                  <v2-input
                    :value="filters.search"
                    :placeholder="searchBarPlaceholderText"
                    @input="filters.search = $event; searchProducts()"
                  />
                </div>

                <!-- Group Products by — next to the search bar. Shown on the
                     public wishlist and for retailers (hidden for brands
                     viewing their own selection). Options come from the facets
                     (Departments/Categories/…) and grouping is applied
                     server-side. On mobile it wraps onto its own row so the
                     search bar keeps its width. -->
                <div
                  v-if="publicToken || !isBrand"
                  class="v2-flex v2-items-center v2-gap-2 v2-wishlist-products-page__group-by"
                >
                  <!-- Desktop-only caption. The dropdown beside it shows the
                       selected value / "None" (#3); on mobile the caption is
                       hidden and the dropdown shows a "Group Products by"
                       placeholder (#4). Label matches the V1 wishlist. -->
                  <span class="v2-text-sm v2-text-muted v2-wishlist-products-page__group-by-label d-none d-lg-block">Group Products by:</span>
                  <!-- Desktop: shows the selected value, or "None" when nothing
                       is grouped (#3). Mobile: shows the "Group Products by"
                       placeholder until a value is chosen, then the chosen value
                       (#4). The per-viewport value comes from groupByDisplayValue. -->
                  <v2-dropdown
                    :options="groupOptions"
                    :value="groupByDisplayValue"
                    placeholder="Group Products by"
                    @select="selectedGroup = $event"
                  />
                </div>

                <!-- Select All -->
                <v2-button
                  v-if="!publicToken && products.length > 0"
                  variant="outline"
                  @click="onToggleSelectAll"
                >
                  <svg
                    v-if="!isSelectedAllOnScreen"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {{ isSelectedAllOnScreen ? 'Deselect all' : 'Select all' }}
                </v2-button>

                <!-- Share Wishlist -->
                <v2-button
                  v-if="isBrand && !isReadOnly"
                  variant="primary"
                  @click="onClickShareWishlist"
                >
                  Share Wishlist
                </v2-button>

                <!-- KP-3861: same dialog as the KP-3462 toolbar above. It has to
                     be in BOTH: the flag is off in production, so shipping it to
                     only one layout hid the feature from everyone there. -->
                <v2-button
                  v-if="isBrand && !isReadOnly"
                  variant="outline"
                  @click="showPreviewSettings = true"
                >
                  Sharing &amp; email preview
                </v2-button>

                <!-- Currency dropdown -->
                <div
                  v-if="isNotPublicTokenWithRetailer && wishlist.availableCurrencies && wishlist.availableCurrencies.length"
                  class="v2-wishlist-products-page__currency"
                >
                  <currency-dropdown
                    :base-currency="wishlistBaseCurrency"
                    :selected-currency="filters.currency"
                    :currencies="wishlist.availableCurrencies || []"
                    @on-selected-currency="filterByCurrency"
                  />
                </div>

                <!-- KP-3360: Filter By Delivery Date. Options come from the
                     public filters endpoint (availabilityDates windows); applied
                     server-side via the availabilityDates query param. Normally
                     desktop-only — on mobile/tablet it moves into the filter
                     sheet (see the #before-filters slot above). But when there
                     are no department facets the sheet's ShopFilter (and that
                     slot) doesn't mount, so the --mobile modifier keeps this
                     control visible on mobile as the only way to filter by date. -->
                <div
                  v-if="filterOptions.availabilityDates && filterOptions.availabilityDates.length"
                  class="v2-wishlist-products-page__dd-desktop"
                  :class="{ 'v2-wishlist-products-page__dd-desktop--mobile': !hasFilterOptions }"
                >
                  <v2-filter-by-delivery-date
                    :availability-dates="filterOptions.availabilityDates"
                    :value="selectedDeliveryDateIndexes"
                    @apply="applyDeliveryDateFilter"
                  />
                </div>
              </div>

              <div class="v2-flex v2-items-center v2-gap-2 v2-wishlist-products-page__right-controls">
                <!-- Filter trigger — only shown below the desktop breakpoint,
                     where the filter is an off-canvas sheet (CSS-gated). -->
                <button
                  v-if="hasFilterOptions"
                  type="button"
                  class="v2-wishlist-products-page__filter-toggle d-inline-flex d-lg-none"
                  @click="openSideBar"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line
                      x1="4"
                      y1="6"
                      x2="20"
                      y2="6"
                    />
                    <line
                      x1="7"
                      y1="12"
                      x2="17"
                      y2="12"
                    />
                    <line
                      x1="10"
                      y1="18"
                      x2="14"
                      y2="18"
                    />
                  </svg>
                  <span class="v2-wishlist-products-page__filter-label">Filter</span>
                  <span
                    v-if="activeFilterCount"
                    class="v2-wishlist-products-page__filter-badge"
                  >{{ activeFilterCount }}</span>
                </button>

                <!-- View Toggle - Grid Icon -->
                <button
                  class="v2-wishlist-products-page__view-btn d-none d-sm-flex"
                  :class="{ 'v2-wishlist-products-page__view-btn--active': itemViewClass === PRODUCTS_VIEW.GRID_VIEW }"
                  @click="onGridViewClick"
                >
                  <large-grid-view-icon />
                </button>

                <!-- View Toggle - Compact Icon -->
                <button
                  class="v2-wishlist-products-page__view-btn d-none d-sm-flex"
                  :class="{ 'v2-wishlist-products-page__view-btn--active': itemViewClass === PRODUCTS_VIEW.COMPACT_GRID_VIEW }"
                  @click="onCompactViewClick"
                >
                  <compact-grid-view-icon />
                </button>

                <!-- Sort -->
                <v2-dropdown
                  :options="visibleSortByOptions"
                  :value="selectedSortBy"
                  label-key="text"
                  value-key="value"
                  align="right"
                  @select="onSortChange"
                />
              </div>
            </div>

            <!-- Product Grid -->
            <div
              v-if="products.length"
              class="v2-wishlist-products-page__products"
            >
              <div
                v-for="(groupedProduct, index) in products"
                :key="index"
              >
                <!-- Group heading -->
                <div
                  v-if="groupedProduct._id && groupedProduct.products.length"
                  class="v2-wishlist-products-page__group-heading"
                >
                  {{ groupedProduct._id }}
                </div>

                <!-- Product cards -->
                <div
                  v-if="groupedProduct.products.length"
                  class="v2-wishlist-products-page__grid"
                  :class="itemViewClass === PRODUCTS_VIEW.COMPACT_GRID_VIEW ? 'v2-wishlist-products-page__grid--compact' : ''"
                >
                  <v2-product-card
                    v-for="product in groupedProduct.products"
                    :key="product.productInfo._id"
                    :product="product"
                    :show-checkbox="isShowWsp && !isReadOnly"
                    :is-checked="indexes.includes(product.productInfo._id)"
                    :is-selection-mode="indexes.length > 0"
                    :show-actions="isShowWsp && !isReadOnly"
                    :actions="getProductActions(product)"
                    :show-wsp="isShowWsp && wspAllowedForViewer"
                    @check="onCheckboxChange($event.product, $event.checked)"
                    @click="onClickWishlistProduct($event)"
                    @action="onProductAction($event)"
                  />
                </div>
              </div>

              <!-- Loading more -->
              <div
                v-if="continueLoading"
                ref="loadingIcon"
                class="v2-flex v2-justify-center v2-p-4"
              >
                <v2-spinner label="Loading more..." />
              </div>
            </div>

            <!-- Empty search -->
            <div
              v-else-if="filters.search"
              class="v2-text-center v2-p-8 v2-text-muted"
            >
              No products found
            </div>
          </div>
        </template>

        <!-- No products at all -->
        <template v-else>
          <v2-card class="v2-wishlist-products-page__empty">
            <h3 class="v2-heading-sm v2-mb-2">
              No Products Added
            </h3>
            <p class="v2-text-sm v2-text-muted v2-mb-4">
              Go to "{{ isNotPublicTokenWithRetailer ? 'Explore' : 'Collections' }}" and start adding some products to your Amazing {{ getWishlistTitle() }}
            </p>
            <v2-button
              variant="primary"
              :disabled="isNotPublicTokenWithRetailer && !isEnabledMarketplace"
              @click="$router.push(isNotPublicTokenWithRetailer ? { name: 'explore' } : { name: 'collections' })"
            >
              {{ isNotPublicTokenWithRetailer ? 'Explore Now' : 'Collections' }}
            </v2-button>
          </v2-card>
        </template>
      </div>

      <!-- Bottom multi-select action bar -->
      <transition name="v2-bar">
        <div
          v-if="selectedCount > 0 && !publicToken"
          class="v2-wishlist-products-page__action-bar"
        >
          <div class="v2-wishlist-products-page__action-bar-inner">
            <!-- Count -->
            <div class="v2-wishlist-products-page__bar-item v2-wishlist-products-page__bar-count">
              <span class="v2-wishlist-products-page__bar-badge">{{ selectedCount }}</span>
              {{ selectedCount === 1 ? 'Product selected' : 'Products selected' }}
            </div>

            <!-- Select all -->
            <button
              class="v2-wishlist-products-page__bar-item"
              @click="onBarSelectAll"
            >
              Select all
            </button>

            <!-- Deselect all -->
            <button
              class="v2-wishlist-products-page__bar-item"
              @click="onBarDeselectAll"
            >
              Deselect all
            </button>

            <!-- Add to cart (retailer only, conditional) -->
            <button
              v-if="isRetailer && isOrderEnableInWishlist && !isReadOnly"
              class="v2-wishlist-products-page__bar-item v2-flex v2-items-center v2-gap-1"
              @click="onBarAddToCart"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle
                  cx="9"
                  cy="21"
                  r="1"
                />
                <circle
                  cx="20"
                  cy="21"
                  r="1"
                />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              Add to cart
            </button>

            <!-- Download products -->
            <button
              class="v2-wishlist-products-page__bar-item v2-flex v2-items-center v2-gap-1"
              @click="onBarDownload"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line
                  x1="12"
                  y1="15"
                  x2="12"
                  y2="3"
                />
              </svg>
              Download products
            </button>

            <!-- Remove (retailer: "from wishlist", brand: "from selection") -->
            <button
              class="v2-wishlist-products-page__bar-item v2-wishlist-products-page__bar-item--danger v2-flex v2-items-center v2-gap-1"
              :disabled="isRemovingFromBar"
              @click="onBarRemove"
            >
              <v2-spinner
                v-if="isRemovingFromBar"
                size="sm"
              />
              <template v-else>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
                {{ isRetailer ? 'Remove from wishlist' : 'Remove from selection' }}
              </template>
            </button>
          </div><!-- /__action-bar-inner -->
        </div><!-- /__action-bar -->
      </transition>

      <!-- Share wishlist action -->
      <v2-wishlist-action
        v-if="wishlist._id"
        ref="wishlistAction"
        :wishlist="wishlist"
        @fetchWishlists="fetchShopProducts"
      />

      <!-- KP-3861: price visibility + email-preview selection -->
      <v2-wishlist-preview-settings-modal
        v-if="wishlist._id"
        :show="showPreviewSettings"
        :wishlist="wishlist"
        :products="previewCandidateProducts"
        @close="showPreviewSettings = false"
        @saved="onPreviewSettingsSaved"
      />

      <!-- Product Detail Modal (V2) -->
      <V2ProductDetailModal
        v-if="modalProductId"
        :visible="!!modalProductId"
        :product-id="modalProductId"
        :collection-id="modalCollectionId"
        :wishlist-id="$route.params.wishlistId"
        @close="modalProductId = null; modalCollectionId = null"
        @deleted="onProductDeleted"
      />
    </div>
  </div>
</template>

<script>
import { getColorCode } from '@/product-colors'
import {
  formatObject,
  getDefaultProductPlaceholderImage,
  getWishlistTitle,
} from '@core/utils/utils'
import store from '@/store'
import analytics, {
  getSelectionOrWishlistMixpanelObj,
} from '@/@core/utils/analytics'
import constants, {
  wishlistAction,
  PRODUCTS_VIEW,
  marketplaceAccessDeniedMessage,
  FLAG_CONSTANTS,
 DOWNLOAD_TYPES, FILE_FORMATS,
} from '@/constants'
import {
  UPDATE_IS_SELECTED_ALL_PRODUCTS,
  UPDATE_SELECT_COUNT,
  UPDATE_SELECT_TYPE,
  MULTI_SELECT_PAGES,
} from '@/store/modules/shop.module'
import { DOWNLOAD_COLLECTION } from '@/store/modules/collection.module'
import { SHOW_DOWNLOADS_NAV_INDICATOR } from '@/store/modules/notification.module'

import { apiToastSuccess } from '@core/utils/toast'
import { mapState } from 'vuex'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Dropdown from '@/components/v2/V2Dropdown.vue'
import V2ProductCard from '@/components/v2/V2ProductCard.vue'
import CurrencyDropdown from '@/views/components/currency-dropdown/CurrencyDropdown.vue'
import ProductImageMixin from '@/views/mixins/ProductImageMixin.vue'
import {
  useWishlistProducts, sortByOptions, sortOptionsFor, searchBarPlaceholderText,
} from '@/composables/useWishlistProducts'
import LargeGridViewIcon from '@/@core/assets/svg-components/LargeGridViewIcon.vue'
import CompactGridViewIcon from '@/@core/assets/svg-components/CompactGridViewIcon.vue'
import V2FilterByDeliveryDate from '@/components/v2/V2FilterByDeliveryDate.vue'
import ShopFilter from '../../apps/shop/ShopFilter.vue'
import UserRoleMixin from '../../apps/UserRoleMixin.vue'
import V2WishlistAction from './V2WishlistAction.vue'
import V2WishlistPreviewSettingsModal from './V2WishlistPreviewSettingsModal.vue'
import V2ProductDetailModal from '../product/V2ProductDetailModal.vue'

const { BRAND_ACCESS } = c
const { CONNECTION_REQUEST_STATUS, TRACKS } = constants
const { MULTI_SELECT_PRODUCTS } = TRACKS.ACTIONS

export default {
  name: 'V2WishlistProducts',
  components: {
    V2Spinner,
    V2Card,
    V2Button,
    V2Input,
    V2Dropdown,
    V2ProductCard,
    CurrencyDropdown,
    LargeGridViewIcon,
    CompactGridViewIcon,
    ShopFilter,
    V2WishlistAction,
    V2WishlistPreviewSettingsModal,
    V2ProductDetailModal,
    V2FilterByDeliveryDate,
  },
  mixins: [UserRoleMixin, ProductImageMixin],
  props: {
    select: Boolean,
    publicToken: {
      type: String,
      default: null,
    },
  },
  setup(props, { root }) {
    const wp = useWishlistProducts(props, root)
    return { ...wp }
  },
  data() {
    return {
      PRODUCTS_VIEW,
      itemViewClass: PRODUCTS_VIEW.GRID_VIEW,
      isRemovingFromBar: false,
      selectedSortBy: sortByOptions[0],
      getColorCode,
      getDefaultProductPlaceholderImage,
      getWishlistTitle,
      marketplaceAccessDeniedMessage,
      searchBarPlaceholderText,
      sortByOptions,
      modalProductId: null,
      modalCollectionId: null,
      // KP-3861 — "Sharing & email preview" dialog.
      showPreviewSettings: false,
      // KP-3360 — indices into filterOptions.availabilityDates that are selected
      // in the Filter By Delivery Date dropdown.
      selectedDeliveryDateIndexes: [],
      // KP-3360 — mobile: the header collapses (name line slides away) on
      // scroll-down and expands on scroll-up.
      isHeaderCollapsed: false,
      headerScrollTicking: false,
      // Group-by label differs by viewport: desktop shows the value ("None"
      // when ungrouped), mobile shows a "Group by" placeholder. lg = 960px.
      isDesktopView: typeof window !== 'undefined' ? window.innerWidth >= 960 : true,
    }
  },
  computed: {
    // KP-4586: no price sort on a public link priced from a chosen book.
    visibleSortByOptions() {
      return sortOptionsFor(this.sortByOptions, this.hasPriceSource)
    },

    // KP-3861: products the brand can pick for the email preview.
    //
    // Flattened out of the grouped shape the page renders, because the picker
    // cares about the wishlist's products, not how the current group-by has
    // bucketed them.
    // `products` is a composition-API ref that is not always auto-unwrapped to
    // an array here (same caveat onProductDeleted guards for), and this runs
    // during render — an unguarded flatMap would throw mid-template.
    previewCandidateProducts() {
      if (!Array.isArray(this.products)) return []
      return this.products.flatMap(group => group?.products || [])
    },

    // KP-3462: when ON, render the space-reclaiming layout (full-width toolbar
    // with the title inside, denser grid). OFF = original header + in-content
    // controls bar. Gated behind the B2B Flagsmith flag.
    isKp3462Layout() {
      const hasFeature = this.$store?.getters?.['flags/hasFeature']
      return typeof hasFeature === 'function'
        ? hasFeature(FLAG_CONSTANTS.KP_3462_COLLAPSIBLE_SIDEBAR)
        : false
    },
    selectedCount() {
      return this.indexes.length
    },
    isOrderEnableInWishlist() {
      if (this.wishlist?.isPremium) {
        return this.requestStatus === CONNECTION_REQUEST_STATUS.APPROVED
      }
      return true
    },
    isNotPublicTokenWithRetailer() {
      return !this.publicToken && this.isRetailer
    },
    // True once the facets are populated. Gates the whole sidebar/sheet (and is
    // the complement of the loading skeleton's `!hasFilterOptions`), so the two
    // flip on the same signal — no layout shift, and wishlists without
    // department facets still get their category/size/price filters. Empty until
    // hydration, then always true (buildPublicFilterOptions returns price bounds
    // even when every facet array is empty).
    hasFilterOptions() {
      return Object.keys(this.filterOptions)?.length > 2
    },
    // Count of selected filter options across all facet arrays (departments,
    // categories, colours, sizes, availabilityDates, …) — shown as a badge on
    // the mobile Filter button so users know filters are active without opening
    // the sheet. Non-facet keys (paging/search/sort/currency) are ignored.
    activeFilterCount() {
      const f = this.filters || {}
      // Price ranges are stored as numeric min/max (not arrays) — exclude them
      // from the array tally and count each active range once below.
      const skip = [
        'page', 'limit', 'search', 'sortBy', 'sort', 'currency', 'groupBy', 'wishlistId',
        'retailPriceMin', 'retailPriceMax', 'wholesalePriceMin', 'wholesalePriceMax',
        'retailPrice', 'wholesalePrice',
      ]
      let count = Object.keys(f).reduce((acc, key) => {
        if (skip.includes(key)) return acc
        const value = f[key]
        return Array.isArray(value) ? acc + value.length : acc
      }, 0)
      const hasRange = (min, max) => typeof min === 'number' || typeof max === 'number'
      if (hasRange(f.retailPriceMin, f.retailPriceMax)) count += 1
      if (hasRange(f.wholesalePriceMin, f.wholesalePriceMax)) count += 1
      return count
    },
    isShowWsp() {
      return !(
        this.isWspHidden
        && this.requestStatus !== CONNECTION_REQUEST_STATUS.APPROVED
      )
    },

    ...mapState({
      profile: state => state.auth.profileData,
    }),
    isEnabledMarketplace() {
      return this.profile?.access?.includes(BRAND_ACCESS.MARKETPLACE)
    },
    show() {
      return this.indexes.length > 0
    },
    groupOptions() {
      if (!this.groups || !Array.isArray(this.groups)) return ['None']
      return this.groups
    },
    // Value fed to the Group-by dropdown. Desktop passes the raw selection so it
    // renders "None"/the chosen value (#3); mobile maps the default 'None' to an
    // empty value so the dropdown shows its "Group by" placeholder (#4). Either
    // way a real selection is shown verbatim.
    groupByDisplayValue() {
      if (this.isDesktopView) return this.selectedGroup
      return this.selectedGroup === 'None' ? '' : this.selectedGroup
    },
    productList() {
      return this.products.flatMap(groupIndex => groupIndex.products.filter(product => this.indexes.includes(product.productInfo._id)))
    },
  },
  watch: {
    selectedSortBy() {
      let sort = this.selectedSortBy.value
      if (this.publicToken && sort === 'highest') sort = 'highest_retail'
      if (this.publicToken && sort === 'lowest') sort = 'lowest_retail'
      if (this.publicToken) {
        this.filters.sort = sort
      } else {
        this.filters.sortBy = sort
      }
      this.fetchShopProducts()
    },
    // KP-3360 — keep the Delivery Date control in sync when the filter is
    // cleared elsewhere (e.g. ShopFilter "Reset Filters" empties
    // availabilityDates). Without this the chip/badge stays active while the
    // results are already unfiltered.
    'filters.availabilityDates': function onAvailabilityDatesChange(val) {
      if (!Array.isArray(val) || !val.length) {
        this.selectedDeliveryDateIndexes = []
      }
    },
  },
  created() {
    this.$root.$refs.wishlist = this
    store.commit(UPDATE_SELECT_TYPE, MULTI_SELECT_PAGES.WISHLIST)
    this.filters.sort = this.selectedSortBy.value
    this.fetchShopProducts()
    window.addEventListener('scroll', this.onHeaderScroll, { passive: true })
    window.addEventListener('resize', this.onViewportResize, { passive: true })
  },
  destroyed() {
    window.removeEventListener('scroll', this.onHeaderScroll)
    window.removeEventListener('resize', this.onViewportResize)
    // (drawer body-class cleanup is centralised in useWishlistProducts' onUnmounted)
    store.commit(UPDATE_SELECT_COUNT, 0)
    store.commit(UPDATE_IS_SELECTED_ALL_PRODUCTS, false)
    analytics.track(
      this.isBrand
        ? constants.TRACKS.ACTIONS.BRAND_END_VIEWING_SELECTION
        : constants.TRACKS.ACTIONS.RETAILER_END_VIEWING_WISHLIST,
      formatObject(
        getSelectionOrWishlistMixpanelObj(this.wishlist, this.isBrand),
      ),
    )
  },
  methods: {
    // KP-3861 — refetch after the brand changes price visibility, so their own
    // view reflects the new setting without a manual reload.
    onPreviewSettingsSaved() {
      this.fetchShopProducts()
    },

    // KP-3360 — apply the Filter By Delivery Date selection. The dropdown emits
    // indices into filterOptions.availabilityDates; map them back to the
    // "MM/YYYY-MM/YYYY" window strings, set the filter, and refetch (page 0).
    // buildFetchParam forwards `availabilityDates` (comma-joined) to the products
    // endpoint, which the catalog-service filters on server-side.
    applyDeliveryDateFilter(indexes) {
      this.selectedDeliveryDateIndexes = [...indexes]
      const options = this.filterOptions.availabilityDates || []
      this.filters.availabilityDates = indexes
        .map(i => options[i])
        .filter(Boolean)
      this.filters.page = 0
      this.fetchShopProducts()
      // Track both brand (selection) and retailer (wishlist) cases. analytics.track
      // is a no-op for guests, so the public/unauthenticated view is skipped.
      analytics.track(
        this.isBrand
          ? constants.TRACKS.ACTIONS.BRAND_APPLIES_DELIVERY_DATES_FILTER
          : constants.TRACKS.ACTIONS.RETAILER_APPLIES_DELIVERY_DATES_FILTER,
        {
          deliveryDates: this.filters.availabilityDates,
          wishlistId: this.wishlist?._id,
        },
      )
    },
    // KP-3360 — collapse the mobile header on scroll-down, expand on scroll-up.
    // Position hysteresis with a wide dead-zone so the header's own height
    // change can't bounce it (no flicker). Inert on desktop (CSS-gated).
    onHeaderScroll() {
      if (this.headerScrollTicking) return
      this.headerScrollTicking = true
      window.requestAnimationFrame(() => {
        const y = Math.max(0, window.scrollY || window.pageYOffset || 0)
        if (!this.isHeaderCollapsed && y > 140) {
          this.isHeaderCollapsed = true
        } else if (this.isHeaderCollapsed && y < 70) {
          this.isHeaderCollapsed = false
        }
        this.headerScrollTicking = false
      })
    },
    // Track the desktop/mobile boundary (lg = 960px) so the Group-by label
    // switches between the selected value (desktop) and the "Group by"
    // placeholder (mobile) live as the window is resized.
    onViewportResize() {
      this.isDesktopView = window.innerWidth >= 960
    },
    getProductActions(product) {
      const actions = [
        { key: 'remove', label: 'Remove', variant: 'ghost' },
      ]
      if (this.isNotPublicTokenWithRetailer) {
        actions.push({
          key: 'cart',
          label: this.isProductInCart(product) ? 'View In Cart' : 'Move to Cart',
          variant: this.isProductInCart(product) ? 'secondary' : 'primary',
          disabled: product.productInfo.price === 'N/A',
        })
      }
      return actions
    },
    onProductAction({ action, product }) {
      if (action === 'remove') {
        this.removeProductFromWishlistClick(product)
      } else if (action === 'cart') {
        this.handleWishlistCartActionClick(product, this.wishlistId)
      }
    },
    onCheckboxChange(product, isChecked) {
      const productId = product.productInfo._id
      if (isChecked) {
        if (!this.indexes.includes(productId)) {
          this.indexes = [...this.indexes, productId]
          analytics.track(
            MULTI_SELECT_PRODUCTS.USER_SELECTS_PRODUCT_BY_CHECKBOX_IN_WISHLIST_PRODUCTS_PAGE,
            { product },
          )
        }
      } else {
        this.indexes = this.indexes.filter(id => id !== productId)
        analytics.track(
          MULTI_SELECT_PRODUCTS.USER_DESELECTS_PRODUCT_BY_CHECKBOX_IN_WISHLIST_PRODUCTS_PAGE,
          { product },
        )
      }
    },
    goBack() {
      this.$router.push({ name: this.isBrand ? 'v2-selection' : 'v2-wishlist' })
    },
    onClickShareWishlist() {
      if (this.$refs.wishlistAction) {
        this.$refs.wishlistAction.openAction('share')
      }
      analytics.track(
        constants.TRACKS.ACTIONS.BRAND_OPENS_SHARE_SELECTION_POPUP,
        { wishlist: this.wishlist },
      )
    },
    isProductInCart(product = null) {
      return product?.isInCart || product?.productInfo?.isInCart
    },
    onClickWishlistProduct(product) {
      // Public link is a standalone, unauthenticated view — products are not
      // openable (no detail modal / app navigation).
      if (this.publicToken) return
      this.modalProductId = product.productInfo._id
      this.modalCollectionId = product.productInfo.collectionId
      analytics.track(
        constants.TRACKS.PAGES.RETAILER_OPENS_WISHLIST_PRODUCT,
        formatObject(getSelectionOrWishlistMixpanelObj(product, false)),
      )
    },
    onProductDeleted({ productId }) {
      // Cheap state cleanup first so it always succeeds regardless of grid shape.
      this.modalProductId = null
      this.modalCollectionId = null
      this.indexes = this.indexes.filter(id => id !== productId)
      // Drop the deleted product from the rendered grid (composition-API ref `products`,
      // grouped as [{ _id, products: [...] }]) so the row vanishes without a refetch.
      // `productList` is a computed derived from `indexes` + `products`, so it
      // refreshes automatically — do not assign to it.
      // Defensive guard: in some test environments the ref isn't auto-unwrapped to an
      // array, so the iteration is a no-op rather than throwing.
      if (Array.isArray(this.products)) {
        this.products.forEach(group => {
          if (Array.isArray(group?.products)) {
            const idx = group.products.findIndex(p => p?.productInfo?._id === productId)
            if (idx !== -1) group.products.splice(idx, 1)
          }
        })
      }
    },
    onGridViewClick() {
      this.itemViewClass = PRODUCTS_VIEW.GRID_VIEW
      analytics.track(
        this.isBrand
          ? constants.TRACKS.ACTIONS.BRAND_SELECTS_GENERAL_VIEW
          : constants.TRACKS.ACTIONS.RETAILER_SELECTS_GENERAL_VIEW,
        { wishlistId: this.wishlist?._id },
      )
    },
    onCompactViewClick() {
      this.itemViewClass = PRODUCTS_VIEW.COMPACT_GRID_VIEW
      analytics.track(
        this.isBrand
          ? constants.TRACKS.ACTIONS.BRAND_SELECTS_COMPACT_VIEW
          : constants.TRACKS.ACTIONS.RETAILER_SELECTS_COMPACT_VIEW,
        { wishlistId: this.wishlist?._id },
      )
    },
    onSortChange(val) {
      const opt = this.sortByOptions.find(o => o.value === val)
      if (opt) this.selectedSortBy = opt
    },
    async downloadSelectedWishlistProducts() {
      try {
        const payload = {
          downloadType: DOWNLOAD_TYPES.COLLECTION_EXPORT.VALUE,
          respectiveId: this.collectionId,
          fileType: FILE_FORMATS.XLSX,
          productIds: this.indexes,
        }
        const res = await this.$store.dispatch(DOWNLOAD_COLLECTION, { payload, params: { isOrderSheetDownload: true } })
        apiToastSuccess(res.data.message)
        this.$store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
        this.forceToggleSelect(false)
      } catch (err) {
        // error toast handled by axios interceptor
      }
    },
    onBarSelectAll() {
      this.forceToggleSelect(true)
      analytics.track(MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_SELECT_ALL, { selectedProductsCount: this.selectedCount })
    },
    onBarDeselectAll() {
      this.forceToggleSelect(false)
      analytics.track(MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_DESELECT_ALL, { selectedProductsCount: this.selectedCount })
    },
    onBarAddToCart() {
      this.moveAllToCart()
      analytics.track(MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_ADD_TO_CART, { selectedProductsCount: this.selectedCount })
    },
    onBarDownload() {
      this.downloadSelectedWishlistProducts()
      analytics.track(MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_DOWNLOAD_PRODUCTS, { selectedProductsCount: this.selectedCount })
    },
    async onBarRemove() {
      this.isRemovingFromBar = true
      await this.removeAllFromWishList()
      this.isRemovingFromBar = false
      analytics.track(
        this.isRetailer
          ? MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_REMOVE_FROM_WISHLIST
          : MULTI_SELECT_PRODUCTS.BOTTOM_TOOLBAR_USER_CLICKS_REMOVE_FROM_SELECTION,
        { selectedProductsCount: this.selectedCount },
      )
    },
    async removeAllFromWishList() {
      const action = wishlistAction.REMOVE_IN_WISHLIST
      await this.reactToggleProductInWishlist(this.productList, this.indexes, action)
      this.forceToggleSelect(false)
    },
    moveAllToCart() {
      this.addMultipleItemsCartActionClick({
        product: this.productList,
        indexes: this.indexes,
      })
      this.forceToggleSelect(false)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/v2/_glass-mixins.scss';
// Bootstrap functions/variables/mixins (no CSS output) — gives media-breakpoint-*.
@import '~@core/scss/base/bootstrap-extended/include';

.v2-wishlist-products-page {
  &__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  // ===== KP-3462: space-reclaiming layout (flag ON only) ====================
  // Scoped to the --kp3462 root modifier so the OFF path (original header +
  // in-content controls bar + 220px grid + default filter scrollbar) is
  // untouched. The frosted toolbar + glass search field already live in the
  // shared &__controls / &__search rules, so only the title-in-toolbar, the
  // denser grid, and the transparent filter scrollbar are gated here.
  &--kp3462 {
    // Back + name inline at the left of the toolbar; occupies the filter-column
    // width (280px) so the search to its right lines up with the product grid.
    .v2-wishlist-products-page__header-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex: 0 0 280px;
      min-width: 0;
    }

    .v2-wishlist-products-page__title-text {
      max-width: 16rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    // Thin, transparent filter scrollbar (no white track); a faint thumb that
    // darkens slightly on hover — lighter/narrower than the default so it
    // doesn't read as a heavy dark-grey bar.
    .v2-wishlist-products-page__sidebar {
      scrollbar-width: thin;
      scrollbar-color: var(--v2-glass-stroke) transparent;

      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-track {
        background: transparent !important;
      }
      &::-webkit-scrollbar-thumb {
        background: var(--v2-glass-stroke);
        border-radius: 999px;
      }
      &::-webkit-scrollbar-thumb:hover {
        background: var(--v2-glass-stroke-strong);
      }
    }

    // Denser grid so more columns fit in the reclaimed iframe width. Scoped to
    // ≥sm (Bootstrap's xs-down boundary is 575.98px) so the phone 2-column
    // layout defined in the media-breakpoint-down(xs) block is preserved.
    @media (min-width: 576px) {
      .v2-wishlist-products-page__grid {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));

        &--compact {
          grid-template-columns: repeat(auto-fill, minmax(125px, 1fr));
        }
      }
    }

    // Desktop (≥md): contained-scroll model mirroring the collection page so
    // the product area scrolls inside a bounded column and products never pass
    // above or show through the top toolbar. Below md, the original page-scroll
    // + sticky collapsing bar is kept (the mobile bottom-sheet filter and
    // scroll-collapse header rely on the window scrolling).
    @media (min-width: 768px) {
      height: calc(100vh - var(--v2-space-6) * 2);
      display: flex;
      flex-direction: column;
      overflow: hidden;

      .v2-wishlist-products-page__loaded {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      // Toolbar sits at the top of the column (not sticky over the page) and
      // carries the page's flat glass fill — matching the collection bar. With
      // the products scrolling below it, the translucent bar shows only the
      // page background behind it, never the products.
      .v2-wishlist-products-page__controls {
        position: static;
        flex-shrink: 0;
        background: var(--v2-glass-sheen), var(--v2-glass-surface-1);
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        border-color: var(--v2-glass-stroke);
      }

      .v2-wishlist-products-page__main {
        flex: 1;
        min-height: 0;
        overflow: hidden;
      }

      // Product column becomes the scroll container (collection parity).
      .v2-wishlist-products-page__content {
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow-y: auto;
      }

      // Filter column fills the bounded row and scrolls internally rather than
      // sticking to the viewport (there's no page scroll to stick to now).
      .v2-wishlist-products-page__sidebar {
        position: static;
        max-height: none;
        align-self: stretch;
      }
    }
  }

  &__back-btn {
    background: none;
    border: 1px solid var(--v2-border);
    border-radius: 0.5rem;
    padding: 0.375rem;
    cursor: pointer;
    color: var(--v2-foreground);
    display: flex;
    align-items: center;

    &:hover {
      background: var(--v2-muted);
    }
  }

  &--disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  // Public link: products are read-only (the detail modal is disabled), so the
  // cards must not look clickable.
  &--public {
    ::v-deep .v2-product-card__clickable {
      cursor: default;
    }
  }

  // KP-3289: public header — wishlist name folded into the single header bar.
  &__brand-title {
    font-family: var(--v2-font-sans);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 280px;
  }

  // Desktop: the title-row wrapper is transparent (name flows inline as before).
  // The style count's show/hide is handled by Bootstrap classes on the element
  // (d-inline-block d-sm-none) — see the template.
  &__titlerow { display: contents; }

  &__brand-divider {
    width: 1px;
    align-self: stretch;
    margin: 2px var(--v2-space-1);
    background: var(--v2-glass-stroke);
  }

  // ===== Filter sidebar skeleton (desktop only) =============================
  // Same footprint as the real __sidebar so swapping to the live filter causes
  // no layout shift. Hidden below the desktop breakpoint, where the filter is
  // an off-canvas sheet rather than a persistent column.
  &__filter-skeleton {
    width: 280px;
    flex-shrink: 0;
    margin-right: var(--v2-space-4);
    align-self: flex-start;
    padding: var(--v2-space-4);
    border-radius: var(--v2-radius-lg);
    background: var(--v2-glass-surface-1);
    border: 1px solid var(--v2-glass-stroke);
    box-shadow: var(--v2-glass-shadow);

    @include media-breakpoint-down(md) {
      display: none;
    }
  }

  &__sk-block {
    margin-bottom: var(--v2-space-5);

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__sk-title,
  &__sk-line,
  &__sk-slider {
    border-radius: 6px;
    background: var(--v2-glass-surface-2);
    background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.55) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    background-size: 200% 100%;
    background-repeat: no-repeat;
    animation: v2-sk-shimmer 1.4s ease-in-out infinite;
  }

  &__sk-title {
    height: 14px;
    width: 45%;
    margin-bottom: 16px;
  }

  &__sk-line {
    height: 12px;
    margin-bottom: 11px;

    &:nth-child(2) { width: 92%; }
    &:nth-child(3) { width: 78%; }
    &:nth-child(4) { width: 85%; }
    &:nth-child(5) { width: 68%; }
  }

  &__sk-slider {
    height: 8px;
    width: 100%;
    margin-top: var(--v2-space-2);
  }

  &__sidebar {
    width: 280px;
    flex-shrink: 0;
    margin-right: var(--v2-space-4);
    // Pin the filter so only the product grid scrolls (V1 parity). The filter
    // scrolls internally when it is taller than the viewport. Aligned with the
    // floating header's top offset.
    position: sticky;
    top: var(--v2-space-3);
    align-self: flex-start;
    max-height: calc(100vh - var(--v2-space-3));
    overflow-y: auto;

    // Skin the legacy ShopFilter (src/views/apps/shop/ShopFilter.vue) with
    // glass styling without modifying the component itself. ShopFilter
    // renders a bootstrap-vue <b-card> inside `.sidebar-shop`; we target
    // that .card via deep selectors so the constraint "do not modify legacy
    // components" is preserved while the surface still reads as v2 glass.
    ::v-deep .sidebar-shop > .card {
      background: var(--v2-glass-surface-1);
      border: 1px solid var(--v2-glass-stroke);
      border-radius: var(--v2-radius-lg);
      box-shadow: var(--v2-glass-shadow);
    }

    ::v-deep .card-body {
      background: transparent;
    }

    // Skin bootstrap-vue's b-form-checkbox (.custom-checkbox /
    // .custom-control-label::before) so the legacy ShopFilter checkboxes
    // match V2Checkbox visually.
    // Match V2Checkbox glass recipe — surface-2 + glass-stroke-strong for
    // visible translucent box, solid violet on check.
    ::v-deep .custom-control-label::before {
      background: var(--v2-glass-surface-2);
      border: 1.5px solid var(--v2-glass-stroke-strong);
    }

    ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
      background: var(--v2-primary);
      border-color: var(--v2-primary);
      backdrop-filter: none;
    }

    ::v-deep .custom-control-input:focus ~ .custom-control-label::before {
      box-shadow: 0 0 0 2px rgba(116, 125, 238, 0.15);
      border-color: var(--v2-glass-stroke-strong);
    }

    // Force long unbreakable strings (e.g. image URLs in the Sub Lines
    // filter) to wrap inside the sidebar instead of overflowing the card.
    // Clamps to 2 lines by default; on hover/focus the full label expands
    // — a CSS-only equivalent of "show more / show less" since the legacy
    // ShopFilter component renders these labels and we cannot modify it.
    ::v-deep .custom-checkbox {
      min-width: 0;
    }

    ::v-deep .custom-control-label {
      word-break: break-word;
      overflow-wrap: anywhere;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      cursor: pointer;
      transition: -webkit-line-clamp 0.15s ease;
    }

    ::v-deep .custom-control:hover .custom-control-label,
    ::v-deep .custom-control:focus-within .custom-control-label {
      -webkit-line-clamp: unset;
    }

    ::v-deep .filter-title {
      margin-top: 1.5rem;
      margin-bottom: 0.75rem;
    }

    ::v-deep .read-more {
      display: inline-block;
      margin-bottom: 0.25rem;
    }

    // KP-3289: breathing room between the Color Family swatch and its label.
    ::v-deep .product-color {
      margin-right: var(--v2-space-2);
      flex-shrink: 0;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--v2-space-3);
    margin-bottom: var(--v2-space-4);
    padding: var(--v2-space-3);
    // Top-level filter/control bar — canonical glass recipe (mixin).
    @include v2-glass-panel-flat;
    // Float the single header bar a touch below the top edge when it sticks, so
    // it reads as a floating glass bar instead of jamming against the viewport.
    position: sticky;
    top: var(--v2-space-3);
    z-index: 20;
    // Real glassmorphism: a genuinely translucent fill + strong backdrop blur,
    // so the products scrolling underneath show through as a frosted blur
    // (rather than the near-opaque white of the flat glass recipe).
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(20px) saturate(150%);
    -webkit-backdrop-filter: blur(20px) saturate(150%);
    border: 1px solid var(--v2-glass-stroke-strong);
    border-radius: var(--v2-radius-lg);
  }

  &__left-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--v2-space-3);
    flex: 1;
  }

  &__right-controls {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    margin-left: auto;
  }

  &__search {
    position: relative;
    flex: 0 0 280px;

    // Glassmorphic search field — nearly clear so the frosted header bar and
    // the blurred products behind it read straight through the input (no opaque
    // fill). A faint inset keeps the field shape legible.
    ::v-deep .v2-input {
      padding-left: var(--v2-space-8);
      background: rgba(255, 255, 255, 0.12);
      border: 1px solid var(--v2-glass-stroke-strong);
      box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);

      &:focus {
        background: rgba(255, 255, 255, 0.22);
      }
    }
  }

  &__search-icon {
    position: absolute;
    top: 50%;
    left: var(--v2-space-3);
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;
    color: var(--v2-muted-foreground);
  }

  &__view-btn {
    // Display/show-hide via Bootstrap classes on the element (d-none d-sm-flex).
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    // Glass card — view-toggle icon buttons (grid / compact-grid). Matches
    // V2CollectionProducts treatment so the two pages feel consistent.
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    background: var(--v2-glass-surface-2);
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover {
      background: var(--v2-glass-surface-1);
      border-color: var(--v2-glass-stroke-strong);
      color: var(--v2-foreground);
    }

    &--active {
      background: var(--v2-primary);
      color: var(--v2-primary-foreground);
      border-color: var(--v2-primary);
      backdrop-filter: none;

      &:hover {
        background: var(--v2-primary);
        color: var(--v2-primary-foreground);
      }
    }

    svg {
      width: 22px;
      height: 22px;
    }
  }

  &__currency {
    flex: 0 0 auto;
  }

  // Group-by sits inline next to the search on desktop. On ≤md the existing
  // controls-reflow rules give the search a full-width row, and since
  // __left-controls wraps, the group-by drops onto its own row beneath it —
  // so the search never gets squeezed. No extra breakpoint needed here.
  &__group-by {
    flex: 0 0 auto;

    &-label {
      white-space: nowrap;
    }
  }

  &__group-heading {
    font-family: var(--v2-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    padding: var(--v2-space-3) 0;
    border-bottom: 1px solid var(--v2-border);
    margin-bottom: var(--v2-space-3);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: var(--v2-space-4);
    margin-bottom: var(--v2-space-4);

    &--compact {
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      gap: var(--v2-space-2);
    }
  }

  &__empty {
    width: 100%;
    padding: var(--v2-space-8);
    text-align: center;
  }

  &__action-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 200;
    display: flex;
    justify-content: center;
  }

  // Light glass floating bar — uses --v2-glass-surface-1 (panel recipe,
  // ~4% tint) so the bar reads as visibly translucent glass instead of
  // near-opaque white. Combined with backdrop-filter blur, the page mesh
  // shows through and the bar feels like proper frosted glass.
  &__action-bar-inner {
    display: flex;
    align-items: center;
    background: var(--v2-glass-surface-1);
    backdrop-filter: blur(var(--v2-glass-blur-lg)) saturate(180%);
    color: var(--v2-foreground);
    border: 1px solid var(--v2-glass-stroke);
    border-bottom: none;
    border-top-left-radius: var(--v2-radius-lg);
    border-top-right-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-glass-shadow);
    white-space: nowrap;
    overflow: hidden;
  }

  &__bar-item {
    position: relative;
    padding: 20px;
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
    background: transparent;
    border: none;
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    gap: var(--v2-space-1);
    transition: background 0.15s ease;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      right: 0;
      transform: translateY(-50%);
      height: 60%;
      width: 1px;
      background: var(--v2-glass-stroke);
    }

    &:last-child::after {
      display: none;
    }

    &:hover:not(:disabled) {
      background: var(--v2-glass-surface-2);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }

    &--danger {
      color: var(--v2-destructive);
    }
  }

  &__bar-count {
    min-width: 200px;
    font-weight: 600;
    cursor: default;

    &:hover {
      background: transparent;
    }
  }

  &__bar-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 700;
    margin-right: var(--v2-space-1);
  }

  // ---------------------------------------------------------------------------
  // Responsive: below the desktop breakpoint the 280px sidebar would squash the
  // product grid, so the filter becomes an off-canvas glass bottom-sheet opened
  // from the Filter button, and the grid takes the full width. These affordances
  // are inert (display:none) on desktop, which keeps the desktop layout intact.
  // ---------------------------------------------------------------------------
  // Filter button show/hide via Bootstrap classes (d-inline-flex d-lg-none).
  &__scrim         { display: none; }
  &__sheet-grip    { display: none; }
  &__sheet-foot    { display: none; }
  // Delivery-date filter: desktop renders it inline in the header controls
  // (__dd-desktop, transparent wrapper); the in-sheet copy (__sheet-dd) is
  // hidden until the bottom-sheet breakpoint.
  &__dd-desktop    { display: contents; }
  &__sheet-dd      { display: none; }

  &__filter-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 99px;
    background: var(--v2-primary);
    color: var(--v2-primary-foreground);
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
  }

  // ===== Tablet & mobile (Bootstrap md-down, ≤991.98px): filter bottom-sheet ==
  @include media-breakpoint-down(md) {
    &__sidebar {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      top: auto;
      width: auto;
      margin: 0;
      align-self: auto;
      z-index: 320;
      display: flex;
      flex-direction: column;
      max-height: 86vh;
      border-radius: 24px 24px 0 0;
      border: 1px solid var(--v2-glass-stroke-strong);
      border-bottom: none;
      // Opaque so the sticky close/reset rows + apply footer can share the exact
      // same fill and blend seamlessly (no "card" edges), while fully hiding the
      // filter list that scrolls behind them.
      background: #ffffff;
      box-shadow: 0 -12px 44px rgba(15, 20, 40, 0.22);
      overscroll-behavior: contain;
      // Sheet scrolls vertically only — never let an inner element (e.g. the
      // delivery-date panel) push the whole sheet sideways.
      overflow-x: hidden;
      padding: 0 var(--v2-space-3);
      transform: translateY(105%);
      transition: transform 0.34s cubic-bezier(0.32, 0.72, 0, 1);

      &--open { transform: translateY(0); }

      // Inside the sheet the legacy ShopFilter card is frameless — the sheet
      // itself is the glass surface.
      ::v-deep .sidebar-shop > .card {
        border: none;
        box-shadow: none;
        background: transparent;
      }
      // Room below the last filter so it clears the sticky apply footer.
      ::v-deep .sidebar-shop .card-body {
        padding-bottom: var(--v2-space-6);
      }
      // Keep ShopFilter's close (✕) and the Filter/Reset row pinned to the top
      // of the sheet while the filter list scrolls underneath.
      ::v-deep .sidebar-shop .card-body > div:first-child {
        position: sticky;
        top: 0;
        z-index: 6;
        // Pure white = exact sheet fill, so it reads as part of the sheet (no
        // "card" edge) while still hiding the list that scrolls under it.
        background: #ffffff;
        // Breathing room above the ✕ once it pins to the top edge of the sheet.
        padding-top: var(--v2-space-4);
      }
      ::v-deep .sidebar-shop .card-body .d-flex.align-items-center:has(.shop-filter) {
        position: sticky;
        top: 50px;
        z-index: 5;
        background: #ffffff;
        padding-bottom: 8px;
      }
    }
  }

  // Split from the block above only so each media-breakpoint block stays under
  // CodeScene's "Large Method" line threshold — same breakpoint, same behaviour.
  @include media-breakpoint-down(md) {
    &__sheet-grip {
      display: block;
      flex: none;
      width: 42px;
      height: 4px;
      margin: 10px auto 4px;
      border-radius: 99px;
      background: var(--v2-glass-stroke-strong);
      cursor: grab;
    }

    // Delivery-date filter swaps homes at this breakpoint: out of the header
    // controls, into the filter sheet (above the filter sections). Exception:
    // when there are no department facets the sheet's ShopFilter slot doesn't
    // render, so --mobile keeps the header control visible as the only entry
    // point to the delivery-date filter.
    &__dd-desktop { display: none; }
    &__dd-desktop--mobile { display: contents; }
    &__sheet-dd {
      display: block;
      margin-top: var(--v2-space-3);
      padding-bottom: var(--v2-space-3);
      border-bottom: 1px solid var(--v2-glass-stroke);
      margin-bottom: var(--v2-space-2);

      // Section label, matching ShopFilter's "filter-title" headings.
      .v2-wishlist-products-page__sheet-dd-label {
        display: block;
        margin-bottom: var(--v2-space-2);
        font-family: var(--v2-font-sans);
        font-size: 0.95rem;
        font-weight: 700;
        color: var(--v2-foreground);
      }
      // The control's own stylesheet handles fitting the sheet (full-width
      // trigger + in-flow panel) via its <1024 media query — see
      // V2FilterByDeliveryDate.vue. Slotted-into-a-child scope means parent
      // ::v-deep can't reliably reach the control's internals from here.
    }

    &__sheet-foot {
      display: block;
      position: sticky;
      bottom: 0;
      // Sit above the scrolling filter list (bootstrap's custom checkboxes are
      // positioned, so without an explicit z-index a checkbox row could paint
      // over the apply button). Opaque white + this z-index = the list is fully
      // hidden behind "Show N results".
      z-index: 15;
      flex: none;
      margin: 0 calc(-1 * var(--v2-space-3));
      padding: var(--v2-space-3);
      background: #ffffff;
      border-top: 1px solid var(--v2-glass-stroke);
    }

    &__sheet-apply {
      width: 100%;
      padding: 0.85rem;
      border: none;
      border-radius: 14px;
      background: var(--v2-primary);
      color: var(--v2-primary-foreground);
      font-family: var(--v2-font-sans);
      font-size: 0.95rem;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 6px 18px rgba(116, 125, 238, 0.35);
      transition: filter 0.15s ease;

      &:active { filter: brightness(0.95); }
    }
  }

  @include media-breakpoint-down(md) {
    &__scrim {
      display: block;
      position: fixed;
      inset: 0;
      z-index: 310;
      background: rgba(15, 20, 40, 0.42);
      backdrop-filter: blur(2px);
      opacity: 0;
      visibility: hidden;
      transition: opacity 0.28s ease, visibility 0.28s ease;

      &--show { opacity: 1; visibility: visible; }
    }

    &__filter-toggle {
      // display via Bootstrap class (d-inline-flex d-lg-none) on the element.
      align-items: center;
      gap: var(--v2-space-1);
      margin-right: auto; // anchor it to the left of the controls row
      padding: 0.5rem 0.85rem;
      border: 1px solid var(--v2-glass-stroke-strong);
      border-radius: var(--v2-radius-md);
      background: var(--v2-glass-surface-2);
      color: var(--v2-foreground);
      font-family: var(--v2-font-sans);
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
      transition: background 0.15s ease, border-color 0.15s ease;

      &:hover {
        background: var(--v2-glass-surface-1);
        border-color: var(--v2-primary);
      }

      svg { flex: none; }
    }

    // Controls reflow: search spans the row, the button row sits below it.
    &__left-controls { width: 100%; }
    &__search { flex: 1 1 100%; }
    &__right-controls {
      width: 100%;
      margin-left: 0;
      flex-wrap: wrap;
    }
    &__brand-title { max-width: 100%; }
  }

  // ===== Phones (Bootstrap xs-down, ≤575.98px): slim glass bar, scroll-collapse
  @include media-breakpoint-down(xs) {
    &__grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--v2-space-2);

      &--compact { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    }

    // ===== Option A "Refined Glass" (from the design handoff) =================
    // A glass card holding a collapsing title row (name + count) above a
    // controls row (search + two square icon buttons). Design lavender tokens
    // mapped to our --v2-* theme; app-standard radii (no pills). On scroll the
    // card tightens and the title row slides away, leaving search + 2 buttons.
    &__controls {
      --ctl-h: 46px;
      top: var(--v2-space-2);
      padding: 14px;
      display: flex;
      flex-wrap: wrap;
      gap: 0 9px;
      align-items: center;
      border: 1px solid rgba(255, 255, 255, 0.75);
      border-radius: 20px;
      background: rgba(255, 255, 255, 0.62);
      backdrop-filter: blur(22px) saturate(150%);
      -webkit-backdrop-filter: blur(22px) saturate(150%);
      box-shadow: var(--v2-glass-shadow);
      transition: padding 0.32s cubic-bezier(0.4, 0, 0.2, 1);
    }
    &__left-controls,
    &__right-controls {
      display: contents;
    }

    // Title row — name (left) + style count (right); collapses on scroll-down.
    &__titlerow {
      display: flex;
      flex: 1 1 100%;
      align-items: baseline;
      justify-content: space-between;
      gap: var(--v2-space-2);
      overflow: hidden;
      max-height: 40px;
      opacity: 1;
      margin-bottom: 12px;
      transition: max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1),
        opacity 0.22s ease, margin 0.32s, transform 0.32s;
    }
  }

  // Split to keep each media-breakpoint block under CodeScene's line threshold.
  @include media-breakpoint-down(xs) {
    &__brand-title {
      max-width: 100%;
      font-size: 1.25rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      line-height: 1.2;
    }
    &__count {
      // display via Bootstrap class (d-inline-block d-sm-none) on the element.
      flex: 0 0 auto;
      font-size: 0.78rem;
      font-weight: 600;
      color: var(--v2-muted-foreground);
      white-space: nowrap;
    }
    &__brand-divider { display: none; }   // not used in the Option-A layout

    // Search — own full-width row (the actions row of buttons wraps beneath
    // it), so the search field never gets squeezed alongside the buttons.
    &__search {
      flex: 1 1 100%;
      min-width: 0;
      margin-bottom: 10px;

      ::v-deep .v2-input {
        height: var(--ctl-h);
        display: flex;
        align-items: center;
        padding-top: 0;
        padding-bottom: 0;
        border-radius: 14px;
      }
    }

    // View toggles hidden on phones via Bootstrap class (d-none d-sm-flex) — no
    // dual gallery densities on phones.

  }

  @include media-breakpoint-down(xs) {
    // ===== Actions row: [Group by] [Sort] [Filter] ===========================
    // The three controls wrap onto their own row beneath the full-width search
    // and split it evenly. Order (via flex `order`): Group by, Sort, then
    // Filter last. The whole row collapses on scroll while the title (Selection
    // / styles) and the search stay put. We animate max-height + opacity (no
    // overflow:hidden, so the filter badge isn't clipped and the dropdown menus
    // can still escape their toggles).

    // Group by (1st) — wide button. The dropdown shows "Group by" until a value
    // is chosen (placeholder), then the chosen value.
    &__group-by {
      order: 1;
      flex: 1 1 0;
      min-width: 0;
      gap: 0;
      max-height: var(--ctl-h);
      transition: max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease, margin 0.32s;

      ::v-deep .v2-dropdown { width: 100%; }
    }

    // Filter (3rd / last) — compact icon-only square (~half a normal button) so
    // group-by + sort get the freed width. Active count shown as a corner badge.
    &__filter-toggle {
      order: 3;
      flex: 0 0 var(--ctl-h);
      min-width: 0;
      width: var(--ctl-h);
      height: var(--ctl-h);
      max-height: var(--ctl-h);
      margin-right: 0;
      padding: 0;
      gap: 0;
      justify-content: center;
      border-radius: 14px;
      position: relative;
      transition: max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease, margin 0.32s, padding 0.32s;
    }
    &__filter-label { display: none; }
    &__filter-badge {
      position: absolute;
      top: -3px;
      right: -3px;
      min-width: 16px;
      height: 16px;
      font-size: 0.62rem;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.9);
    }
  }

  // Split to keep each media-breakpoint block under CodeScene's line threshold.
  @include media-breakpoint-down(xs) {
    // Sort (2nd) — labelled dropdown. The bare <v2-dropdown> is the flex item;
    // group-by's dropdown is nested under __group-by so it isn't matched here.
    &__right-controls ::v-deep .v2-dropdown {
      order: 2;
      flex: 1 1 0;
      min-width: 0;
      max-height: var(--ctl-h);
      transition: max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease, margin 0.32s;
    }

    // Both control dropdowns become full-width buttons.
    ::v-deep .v2-dropdown__toggle {
      width: 100%;
      height: var(--ctl-h);
      padding: 0 10px;
      display: inline-flex;
      align-items: center;
      justify-content: space-between;
      gap: 6px;
      border-radius: 14px;
    }
    // Sort keeps its value label; group-by hides it (caption injected above).
    ::v-deep .v2-dropdown__label {
      display: block;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    // Sort/group menus — lift above the products with a solid frosted panel.
    ::v-deep .v2-dropdown__menu {
      z-index: 60;
      min-width: 150px;
      background: rgba(255, 255, 255, 0.97);
      backdrop-filter: blur(18px) saturate(160%);
      -webkit-backdrop-filter: blur(18px) saturate(160%);
      border: 1px solid var(--v2-glass-stroke-strong);
      box-shadow: var(--v2-glass-shadow);
    }

    &__sidebar { max-height: 90vh; }

    // ----- Scroll-collapsed: hide ONLY the actions row. The title (Selection /
    // styles) and the search stay exactly as they are. -----
    &__controls--collapsed {
      .v2-wishlist-products-page__search { margin-bottom: 0; }
      .v2-wishlist-products-page__group-by,
      .v2-wishlist-products-page__filter-toggle,
      .v2-wishlist-products-page__right-controls ::v-deep .v2-dropdown {
        max-height: 0;
        opacity: 0;
        margin: 0;
        padding-top: 0;
        padding-bottom: 0;
        pointer-events: none;
      }
    }
  }
}

// .v2-product-card styles live in src/components/v2/V2ProductCard.vue.
// Earlier this file contained a full duplicate of those styles (legacy
// from before V2ProductCard was extracted), but a parent component's
// scoped CSS does reach a child component's root element in Vue 2 — that
// duplicate was overriding the glass treatment with solid white and is
// the reason wishlist product cards looked non-glass. Removed.

.v2-text-muted { color: var(--v2-muted-foreground); }
.v2-text-destructive { color: var(--v2-destructive); }
.v2-text-center { text-align: center; }

// Slide-in animation for bottom bar (must be outside scoped BEM block)
.v2-bar-enter-active { animation: v2-slide-up 0.3s ease-out; }
.v2-bar-leave-active { animation: v2-slide-up 0.3s ease-in reverse; }

@keyframes v2-slide-up {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}

// Filter-skeleton shimmer sweep (left → right).
@keyframes v2-sk-shimmer {
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
}
</style>
